import { RetrievePollPayload } from "@virtualprogaming/js-public-sdk";
import classNames from "classnames";
import { map, partial } from "lodash";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import { useChatConversationMembers } from "~/queries";
import { Icon, LoadingIcon } from "~/ui/components/icons";
import { useChatAuthAccessToken } from "~/ui/context";
import { useScreenLg, useScreenMd } from "~/utils";

import { Details } from "./details/Details";
import { MembersPopup } from "./members/MembersPopup";
import { MembersPreview } from "./members/MembersPreview";
import { PollPopup } from "./poll/PollPopup";

const limit = 1000;

enum ConversationDetailsPopupTypes {
  Members = "members",
  Poll = "poll",
  Details = "details",
}

export const ConversationDetails: FC<{
  chatId: number;
  isConversationAdmin: boolean;
  isGlobal: boolean;
  isOwner: boolean;
  ownerId: number | undefined;
}> = ({
  chatId,
  isConversationAdmin = false,
  isGlobal = false,
  isOwner = false,
  ownerId,
}) => {
  const isDesktop = useScreenMd();
  const isLarge = useScreenLg();
  const [
    openedMenu,
    setOpenedMenu,
  ] = useState<ConversationDetailsPopupTypes | null>(null);
  const accessToken = useChatAuthAccessToken();
  const ref = useRef<HTMLDivElement>(null);
  const { data: members, isLoading } = useChatConversationMembers(
    chatId,
    accessToken,
    limit
  );

  useEffect(() => {
    const handleOnClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpenedMenu(null);
      }
    };
    document.addEventListener("mousedown", handleOnClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleOnClickOutside);
    };
  }, [ref, setOpenedMenu]);

  const shouldNotShowAction = useMemo(
    () => (isGlobal && !isConversationAdmin) || (!isOwner && !isGlobal),
    [isConversationAdmin, isGlobal, isOwner]
  );

  const memberIds = useMemo(() => {
    const membersData: RetrievePollPayload = { members: [] };
    if (!members?.data) {
      return membersData;
    } else {
      membersData.members = map(members.data, ({ uid }) =>
        uid ? Number(uid.substring(1)) : 0
      ) as number[];
      return membersData;
    }
  }, [members]);

  return isLoading ? (
    <div className="pd-l-md pd-t-md">
      <LoadingIcon size="2x" />
    </div>
  ) : (
    <div
      className={classNames(
        "border-bottom flex items-center",
        isDesktop ? "h-50px" : "h-5"
      )}
    >
      <div className="flex justify-between items-center w-100">
        {members?.data && <MembersPreview members={members.data} />}
        {chatId === 1 ? null : (
          <div className="pointer flex justify-end pd-r-md pd-l-md w-10 gap-md">
            <Icon
              onClick={partial(
                setOpenedMenu,
                ConversationDetailsPopupTypes.Members
              )}
              icon={["fas", "users"]}
              size="1x"
            />

            <Icon
              onClick={partial(
                setOpenedMenu,
                ConversationDetailsPopupTypes.Poll
              )}
              icon={["far", "poll-people"]}
              size="1x"
            />

            <Icon
              icon={["fas", "info-square"]}
              size="1x"
              onClick={partial(
                setOpenedMenu,
                ConversationDetailsPopupTypes.Details
              )}
            />
          </div>
        )}
      </div>

      {openedMenu && (
        <div
          ref={ref}
          className={classNames(
            "absolute bg-dark border z-index-1020 right-10 flex flex-col borderR-05 top-65px",
            isLarge ? "w-30" : isDesktop ? "w-40" : "w-90"
          )}
        >
          {openedMenu === ConversationDetailsPopupTypes.Members && (
            <MembersPopup
              chatId={chatId}
              members={members?.data ?? []}
              onClose={partial(setOpenedMenu, null)}
              shouldNotShowAction={shouldNotShowAction}
              isGlobal={isGlobal}
              isConversationAdmin={isConversationAdmin}
            />
          )}

          {openedMenu === ConversationDetailsPopupTypes.Poll && ownerId && (
            <PollPopup
              onClose={partial(setOpenedMenu, null)}
              chatId={chatId}
              members={memberIds}
              isOwner={isOwner}
              ownerId={ownerId}
              shouldNotShowAction={shouldNotShowAction}
            />
          )}
          {openedMenu === ConversationDetailsPopupTypes.Details && (
            <Details onClose={partial(setOpenedMenu, null)} chatId={chatId} />
          )}
        </div>
      )}
    </div>
  );
};
